import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorId: number | null;
  actorEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: string | null;
  createdAt: string;
}

const ACTION_LABEL: Record<string, string> = {
  'user.invited': '👤 Usuario invitado',
  'user.updated': '👤 Usuario actualizado',
  'user.password_reset': '🔑 Password reset',
  'business_config.updated': '⚙️ Negocio actualizado',
  'integration.credential_set': '🔐 Credencial guardada',
  'integration.credential_deleted': '🔐 Credencial eliminada',
  'os.transitioned': '🛠 OS transición',
  'invoice.voided': '🗑 Factura anulada',
};

export function AuditLogPage() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const list = useQuery({
    queryKey: ['audit', action, entityType, from, to],
    queryFn: async () => {
      const params: Record<string, string | undefined> = {};
      if (action) params.action = action;
      if (entityType) params.entityType = entityType;
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await api.get<{ data: AuditRow[]; nextCursor: string | null }>(
        '/api/audit',
        { params },
      );
      return data.data;
    },
    refetchInterval: 30_000,
  });

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Bitácora de auditoría</h1>
      <p style={{ color: 'var(--color-muted)', marginTop: 0 }}>
        Registro append-only de acciones administrativas sensibles. Útil para forenses
        y cumplimiento.
      </p>

      <div className="card" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Acción</label>
          <input
            className="input"
            placeholder="user, invoice.voided..."
            value={action}
            onChange={(e) => setAction(e.target.value)}
            style={{ width: 200 }}
          />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Entidad</label>
          <select
            className="input"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            style={{ width: 180 }}
          >
            <option value="">Todas</option>
            <option value="User">Usuario</option>
            <option value="BusinessConfig">Negocio</option>
            <option value="IntegrationCredential">Credencial</option>
            <option value="ServiceOrder">OS</option>
            <option value="Invoice">Factura</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Desde</label>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Hasta</label>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 1rem' }}>Fecha</th>
              <th style={{ padding: '0.5rem 1rem' }}>Acción</th>
              <th style={{ padding: '0.5rem 1rem' }}>Entidad</th>
              <th style={{ padding: '0.5rem 1rem' }}>Actor</th>
              <th style={{ padding: '0.5rem 1rem' }}>IP</th>
              <th style={{ padding: '0.5rem 1rem' }}>Detalles</th>
            </tr>
          </thead>
          <tbody>
            {list.data?.map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}>
                  {new Date(r.createdAt).toLocaleString('es-CO')}
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {ACTION_LABEL[r.action] ?? r.action}
                  <br />
                  <code style={{ fontSize: '0.7rem', color: 'var(--color-muted)' }}>{r.action}</code>
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {r.entityType}
                  {r.entityId && <code style={{ fontSize: '0.75rem' }}> #{r.entityId}</code>}
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>{r.actorEmail ?? '—'}</td>
                <td style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                  {r.ipAddress ?? '—'}
                </td>
                <td style={{ padding: '0.5rem 1rem', maxWidth: 280 }}>
                  {r.metadata && (
                    <details>
                      <summary style={{ cursor: 'pointer', color: 'var(--color-muted)' }}>JSON</summary>
                      <pre style={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap', margin: 0 }}>
                        {tryFormatJson(r.metadata)}
                      </pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Sin registros para los filtros aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function tryFormatJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
